/**
 * "Next pairing in Ns" — the arena result card's 5 s countdown (M4 §4).
 *
 * The number is not decoration: 5 s is cc-game-service's
 * `CONSTANTS.ARENA.MIN_POOL_AGE_MS`, the window during which the pairing pass
 * ignores pool entries — i.e. the two players who have just finished really are
 * invisible to the next pass for that long. Keeping the countdown in step with it
 * is what makes the line truthful rather than an animation.
 *
 * It is its OWN leaf because it ticks (UX spec §6 rule 4): the countdown must not
 * re-render the card's message, its chips or its buttons once a second. It holds
 * at 0 — the board swap on the next pairing replaces this card, so there is
 * nothing further to do.
 */
import {useEffect, useState} from 'react';

/** cc-game-service CONSTANTS.ARENA.MIN_POOL_AGE_MS = 5000 ms. */
export const ARENA_REPAIR_COUNTDOWN_S = 5;
const TICK_MS = 1000;

export type NextPairingModel = {
  /** the one line the card shows, already in its final wording */
  label: string;
  /** a paused player is out of the rotation, so the line is a warning */
  paused: boolean;
};

/** the three faces, in priority order. */
export const nextPairingLabel = (paused: boolean, seconds: number): string => {
  if (paused) {
    return '⏸ Pairing paused';
  }
  return seconds > 0
    ? `⏳ Next pairing in ${seconds}s`
    : '⚡ Finding your next game…';
};

export function useNextPairingModel(paused: boolean): NextPairingModel {
  const [seconds, setSeconds] = useState(ARENA_REPAIR_COUNTDOWN_S);

  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds(left => (left > 0 ? left - 1 : 0));
    }, TICK_MS);
    return () => clearInterval(interval);
  }, []);

  return {label: nextPairingLabel(paused, seconds), paused};
}

export default useNextPairingModel;
